import React, { useState } from "react";
import {
  Download,
  FileSpreadsheet,
  Loader2,
  Mail,
  Smartphone,
  User,
} from "lucide-react";
import { Navigate } from "react-router-dom";
import { saveAs } from "file-saver";
import { isAxiosError } from "axios";
import WrapperContainer from "@components/WrapperContainer";
import ContentContainer from "@src/components/ContentContainer";
import PageTopSection from "@src/components/PageTopSection";
import PageTitle from "@src/components/PageTitle";
import {
  PlaylistOverviewHeader,
  PlaylistOverviewHeaderLeft,
  ButtonSection,
} from "../GuestCurator/GuestCurator.styles";
import { Subtitle } from "../SocialMediaPromo/SocialMediaPromo.styles";
import { useRole } from "@src/hooks/useAuth";
import { useNotification } from "@src/context/NotificationContext";
import { downloadClientsCsv } from "@src/apis/clientApis";
import {
  ClientExportFilters,
  getClientsExportErrorMessage,
  validateClientExportFilters,
} from "@src/utils/clientExport";
import { theme } from "@src/theme";
import {
  ActionsRow,
  AdminBadge,
  ColumnCard,
  ColumnHint,
  ColumnIcon,
  ColumnName,
  ColumnsGrid,
  ColumnsSection,
  ContentWrapper,
  DownloadButton,
  ExportPanel,
  FileMeta,
  FilterField,
  FilterInput,
  FilterLabel,
  FiltersGrid,
  FiltersSection,
  FilterSelect,
  HelperText,
  IconBadge,
  PanelDescription,
  PanelHeader,
  PanelHeaderText,
  PanelTitle,
  SectionLabel,
  ValidationError,
} from "./ClientsExport.styles";

const CSV_COLUMNS = [
  { name: "Name", hint: "Client display name", icon: User },
  { name: "Email", hint: "Account email", icon: Mail },
  { name: "Platform", hint: "iOS or Android", icon: Smartphone },
] as const;

const EMPTY_FILTERS: ClientExportFilters = {
  platform: "",
  search: "",
  country: "",
  createdFrom: "",
  createdTo: "",
};

const ClientsExportPage = (): React.JSX.Element => {
  const role = useRole();
  const { addNotification } = useNotification();
  const [filters, setFilters] = useState<ClientExportFilters>(EMPTY_FILTERS);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  if (role !== "admin") {
    return <Navigate to="/" />;
  }

  const updateFilter = <K extends keyof ClientExportFilters>(
    key: K,
    value: ClientExportFilters[K]
  ): void => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setValidationError(null);
  };

  const handleDownload = async (): Promise<void> => {
    const error = validateClientExportFilters(filters);
    if (error) {
      setValidationError(error);
      return;
    }

    setIsDownloading(true);
    setValidationError(null);

    try {
      const response = await downloadClientsCsv(filters);
      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8",
      });
      saveAs(blob, "clients.csv");
      addNotification("Export completed", "success");
    } catch (err) {
      if (isAxiosError(err)) {
        const message = await getClientsExportErrorMessage(err);
        addNotification(message, "error");
        if (err.response?.status === 400) {
          setValidationError(message);
        }
        return;
      }
      addNotification("Failed to generate export. Please try again.", "error");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <WrapperContainer>
      <PageTopSection>
        <PlaylistOverviewHeader>
          <PlaylistOverviewHeaderLeft>
            <ButtonSection>
              <PageTitle>Export Clients</PageTitle>
              <Subtitle>
                Download a CSV file with client name, email, and platform
                (iOS/Android).
              </Subtitle>
            </ButtonSection>
          </PlaylistOverviewHeaderLeft>
        </PlaylistOverviewHeader>
      </PageTopSection>

      <ContentContainer allowScroll>
        <ContentWrapper>
          <ExportPanel>
            <PanelHeader>
              <IconBadge>
                <FileSpreadsheet size={26} />
              </IconBadge>
              <PanelHeaderText>
                <AdminBadge>Admin only</AdminBadge>
                <PanelTitle>Export clients</PanelTitle>
                <PanelDescription>
                  Generate a CSV export of current app clients. Large exports
                  may take a few seconds — keep this page open until the
                  download starts.
                </PanelDescription>
              </PanelHeaderText>
            </PanelHeader>

            <ColumnsSection>
              <SectionLabel>Included columns</SectionLabel>
              <ColumnsGrid>
                {CSV_COLUMNS.map(({ name, hint, icon: Icon }) => (
                  <ColumnCard key={name}>
                    <ColumnIcon>
                      <Icon size={18} />
                    </ColumnIcon>
                    <ColumnName>{name}</ColumnName>
                    <ColumnHint>{hint}</ColumnHint>
                  </ColumnCard>
                ))}
              </ColumnsGrid>
            </ColumnsSection>

            <FiltersSection>
              <SectionLabel>Filters (optional)</SectionLabel>
              <FiltersGrid>
                <FilterField>
                  <FilterLabel htmlFor="platform">Platform</FilterLabel>
                  <FilterSelect
                    id="platform"
                    value={filters.platform ?? ""}
                    onChange={(e) =>
                      updateFilter(
                        "platform",
                        e.target.value as ClientExportFilters["platform"]
                      )
                    }
                    disabled={isDownloading}
                  >
                    <option value="">All platforms</option>
                    <option value="ios">iOS</option>
                    <option value="android">Android</option>
                  </FilterSelect>
                </FilterField>

                <FilterField>
                  <FilterLabel htmlFor="country">Country</FilterLabel>
                  <FilterInput
                    id="country"
                    type="text"
                    maxLength={100}
                    placeholder="e.g. MX"
                    value={filters.country ?? ""}
                    onChange={(e) => updateFilter("country", e.target.value)}
                    disabled={isDownloading}
                  />
                </FilterField>

                <FilterField $fullWidth>
                  <FilterLabel htmlFor="search">Search</FilterLabel>
                  <FilterInput
                    id="search"
                    type="text"
                    maxLength={100}
                    placeholder="Name or email"
                    value={filters.search ?? ""}
                    onChange={(e) => updateFilter("search", e.target.value)}
                    disabled={isDownloading}
                  />
                </FilterField>

                <FilterField>
                  <FilterLabel htmlFor="createdFrom">Created from</FilterLabel>
                  <FilterInput
                    id="createdFrom"
                    type="date"
                    value={filters.createdFrom ?? ""}
                    onChange={(e) =>
                      updateFilter("createdFrom", e.target.value)
                    }
                    disabled={isDownloading}
                  />
                </FilterField>

                <FilterField>
                  <FilterLabel htmlFor="createdTo">Created to</FilterLabel>
                  <FilterInput
                    id="createdTo"
                    type="date"
                    value={filters.createdTo ?? ""}
                    onChange={(e) => updateFilter("createdTo", e.target.value)}
                    disabled={isDownloading}
                  />
                </FilterField>
              </FiltersGrid>
              <HelperText>
                Leave filters empty to export all clients. Only whitelisted
                query params are sent to the API.
              </HelperText>
              {validationError && (
                <ValidationError>{validationError}</ValidationError>
              )}
            </FiltersSection>

            <ActionsRow>
              <DownloadButton
                type="button"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader2
                    size={16}
                    color={theme.colors.gray200}
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                ) : (
                  <Download size={16} />
                )}
                {isDownloading ? "Generating export…" : "Download CSV"}
              </DownloadButton>
              <FileMeta>
                <FileSpreadsheet size={14} />
                clients.csv · UTF-8
              </FileMeta>
            </ActionsRow>
          </ExportPanel>
        </ContentWrapper>
      </ContentContainer>
    </WrapperContainer>
  );
};

export default ClientsExportPage;
